import { parseHex } from './parse-hex'

const FUNCTIONAL_COLOR = /^(rgb|hsl)a?\(([^)]+)\)$/

/**
 * Sets the alpha channel of a color. An existing alpha channel is replaced, not multiplied.
 *
 * @param color - A hex color (`#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa`) or a functional color
 *   (`rgb()`, `rgba()`, `hsl()`, `hsla()`)
 * @param value - Alpha value between 0 and 1
 * @returns An rgba/hsla color string, or the input unchanged when it cannot be parsed
 *
 * @example
 * alpha('#aafbb2', 0.5) // 'rgba(170, 251, 178, 0.5)'
 * alpha('rgba(170, 251, 178, 0.7)', 0.5) // 'rgba(170, 251, 178, 0.5)'
 * alpha('hsl(120, 50%, 50%)', 0.5) // 'hsla(120, 50%, 50%, 0.5)'
 */
export const alpha = (color: string, value: number): string => {
  if (color.startsWith('#')) {
    const rgb = parseHex(color)
    if (rgb) {
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${value})`
    }
  }

  const match = FUNCTIONAL_COLOR.exec(color)
  if (match) {
    const [, model = '', body = ''] = match
    const channels = body.split(',').map((channel) => channel.trim())
    if (channels.length === 3 || channels.length === 4) {
      return `${model}a(${channels.slice(0, 3).join(', ')}, ${value})`
    }
  }

  return color
}
